import React, { useState, useEffect, useCallback, useRef } from 'react';
import API from '../api';
import { Search, BookOpen, Plus, X, ChevronLeft, ChevronRight, Edit2, Trash2, Database, Tag, Layers, Library } from 'lucide-react';
import { useConfirm } from '../components/ConfirmDialog';

export default function PrezzarioPage() {
  const confirm = useConfirm();
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [categorie, setCategorie] = useState([]);
  const [parti, setParti] = useState([]);
  const [cataloghi, setCataloghi] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [parteFilter, setParteFilter] = useState('');
  const [catalogoFilter, setCatalogoFilter] = useState('cer_2025');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ codice: '', descrizione: '', unita_misura: '', prezzo: '', categoria: '', parte: '', tipo: 'personalizzata' });
  const searchTimeoutRef = useRef(null);

  const loadData = useCallback(async (p) => {
    const params = { page: p || page, limit: 50 };
    if (search) params.q = search;
    if (catFilter) params.categoria = catFilter;
    if (parteFilter) params.parte = parteFilter;
    if (catalogoFilter) params.catalogo = catalogoFilter;
    const { data: d } = await API.get('/api/prezzario', { params });
    setData(d);
  }, [page, search, catFilter, parteFilter, catalogoFilter]);

  const reloadFilters = useCallback(() => {
    const cp = catalogoFilter ? `?catalogo=${catalogoFilter}` : '';
    API.get(`/api/prezzario/categorie${cp}`).then(r => setCategorie(r.data));
    API.get(`/api/prezzario/parti${cp}`).then(r => setParti(r.data));
  }, [catalogoFilter]);

  useEffect(() => {
    API.get('/api/prezzario/cataloghi').then(r => setCataloghi(r.data));
    API.get('/api/prezzario/stats').then(r => setStats(r.data));
  }, []);

  useEffect(() => { reloadFilters(); }, [reloadFilters]);

  // Quando si cambia catalogo, reset categoria/parte
  useEffect(() => {
    setCatFilter('');
    setParteFilter('');
    setPage(1);
  }, [catalogoFilter]);

  useEffect(() => { loadData(page); }, [page, catFilter, parteFilter, catalogoFilter]);

  const isFirstSearchRender = useRef(true);
  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return; // Salta il primo render: evita il bug "torna a pagina 1 dopo 400ms al mount"
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => { setPage(1); loadData(1); }, 400);
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [search]);

  const goPage = (p) => { if (p >= 1 && p <= data.pages) setPage(p); };

  const openNew = () => {
    setForm({ codice: '', descrizione: '', unita_misura: '', prezzo: '', categoria: '', parte: '', tipo: 'personalizzata' });
    setEditing(null);
    setShowForm(true);
  };

  const openEdit = (v) => {
    setForm({ codice: v.codice, descrizione: v.descrizione, unita_misura: v.unita_misura, prezzo: String(v.prezzo), categoria: v.categoria, parte: v.parte || '', tipo: v.tipo || 'personalizzata' });
    setEditing(v.id);
    setShowForm(true);
  };

  const save = async () => {
    const payload = { ...form, prezzo: parseFloat(form.prezzo) || 0 };
    if (editing) {
      await API.put(`/api/prezzario/${editing}`, payload);
    } else {
      await API.post('/api/prezzario', payload);
    }
    setShowForm(false);
    loadData(page);
    API.get('/api/prezzario/stats').then(r => setStats(r.data));
    API.get('/api/prezzario/categorie').then(r => setCategorie(r.data));
  };

  const remove = async (id) => {
    if (await confirm({ title: 'Eliminare voce dal prezzario?', message: 'La voce non sarà più disponibile nei nuovi computi e preventivi.', confirmLabel: 'Elimina', variant: 'danger' })) {
      await API.delete(`/api/prezzario/${id}`);
      loadData(page);
      API.get('/api/prezzario/stats').then(r => setStats(r.data));
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

  return (
    <div className="animate-fade-in" data-testid="prezzario-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight flex items-center gap-3">
            <BookOpen size={28} className="text-primary" /> Prezzario
          </h1>
          <p className="text-slate-500 mt-1">Prezzario CER 2025</p>
        </div>
        <button data-testid="add-voce-btn" onClick={openNew}
          className="px-4 py-2 bg-primary text-white font-bold rounded-md hover:bg-primary-hover transition-all active:scale-95 flex items-center gap-2">
          <Plus size={18} /> Nuova Voce
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="flex items-center gap-2 mb-1"><Database size={16} className="text-primary" /><span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Totale Voci</span></div>
            <p className="text-2xl font-black" data-testid="stats-total">{stats.totale?.toLocaleString('it-IT')}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="flex items-center gap-2 mb-1"><BookOpen size={16} className="text-accent-info" /><span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Ufficiali</span></div>
            <p className="text-2xl font-black">{stats.ufficiali?.toLocaleString('it-IT')}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="flex items-center gap-2 mb-1"><Edit2 size={16} className="text-accent-success" /><span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Personalizzate</span></div>
            <p className="text-2xl font-black">{stats.personalizzate?.toLocaleString('it-IT')}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="flex items-center gap-2 mb-1"><Tag size={16} className="text-accent-warning" /><span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Categorie</span></div>
            <p className="text-2xl font-black">{stats.categorie}</p>
          </div>
        </div>
      )}

      {/* Catalogo Switcher */}
      {cataloghi.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-3 mb-4 flex flex-wrap items-center gap-2" data-testid="catalogo-switcher">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
            <Library size={14} /> Catalogo:
          </span>
          {cataloghi.map(c => (
            <button key={c.id} onClick={() => setCatalogoFilter(c.id)} data-testid={`cat-${c.id}`}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all flex items-center gap-1 ${catalogoFilter === c.id ? (c.id === 'cer_2025' ? 'bg-indigo-600 text-white' : c.id === 'aziendale' ? 'bg-green-600 text-white' : 'bg-blue-600 text-white') : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
              {c.nome} <span className="opacity-70">({c.count?.toLocaleString('it-IT')})</span>
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="search-prezzario" placeholder="Cerca per codice o descrizione..." className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50" value={search} onChange={e => { setSearch(e.target.value); }} />
        </div>
        <div className="flex gap-2">
          <select data-testid="filter-parte" className="flex-1 sm:flex-none px-3 py-2.5 border border-slate-200 rounded-md text-sm" value={parteFilter} onChange={e => { setParteFilter(e.target.value); setPage(1); }}>
            <option value="">Tutte le parti</option>
            {parti.map(p => <option key={p.nome} value={p.nome}>{p.nome} ({p.count?.toLocaleString('it-IT')})</option>)}
          </select>
          <select data-testid="filter-categoria" className="flex-1 sm:flex-none px-3 py-2.5 border border-slate-200 rounded-md text-sm" value={catFilter} onChange={e => { setCatFilter(e.target.value); setPage(1); }}>
            <option value="">Tutte le categorie</option>
            {categorie.map(c => <option key={c.nome} value={c.nome}>{c.nome} ({c.count?.toLocaleString('it-IT')})</option>)}
          </select>
        </div>
      </div>

      {/* Results info */}
      <div className="flex items-center justify-between mb-2 text-sm text-slate-500">
        <span>{data.total?.toLocaleString('it-IT')} risultati - Pagina {data.page} di {data.pages}</span>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="prezzario-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider text-slate-500 w-48">Codice</th>
                <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider text-slate-500">Descrizione</th>
                <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider text-slate-500 w-20">U.M.</th>
                <th className="text-right px-4 py-3 font-bold text-xs uppercase tracking-wider text-slate-500 w-28">Prezzo</th>
                <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider text-slate-500 w-32">Tipo</th>
                <th className="text-right px-4 py-3 font-bold text-xs uppercase tracking-wider text-slate-500 w-24">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {data.items?.map(v => (
                <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-2.5 font-mono text-xs font-bold text-primary whitespace-nowrap">{v.codice}</td>
                  <td className="px-4 py-2.5 text-sm">
                    <p className="line-clamp-2">{v.descrizione}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{v.categoria}</p>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{v.unita_misura}</td>
                  <td className="px-4 py-2.5 text-right font-mono font-bold">{fmt(v.prezzo)}</td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${v.tipo === 'personalizzata' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                      {v.tipo === 'personalizzata' ? 'Aziendale' : 'Ufficiale'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => openEdit(v)} className="p-1 hover:bg-slate-100 rounded transition-colors mr-1" title="Modifica">
                      <Edit2 size={14} className="text-slate-400" />
                    </button>
                    <button onClick={() => remove(v.id)} className="p-1 hover:bg-red-50 rounded transition-colors" title="Elimina">
                      <Trash2 size={14} className="text-red-400" />
                    </button>
                  </td>
                </tr>
              ))}
              {data.items?.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">Nessuna voce trovata</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {data.pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4" data-testid="pagination">
          <button onClick={() => goPage(page - 1)} disabled={page <= 1} className="p-2 border border-slate-200 rounded-md hover:bg-slate-50 disabled:opacity-30 transition-colors">
            <ChevronLeft size={16} />
          </button>
          {Array.from({ length: Math.min(7, data.pages) }, (_, i) => {
            let p;
            if (data.pages <= 7) p = i + 1;
            else if (page <= 4) p = i + 1;
            else if (page >= data.pages - 3) p = data.pages - 6 + i;
            else p = page - 3 + i;
            return (
              <button key={p} onClick={() => goPage(p)}
                className={`w-9 h-9 rounded-md text-sm font-bold transition-colors ${p === page ? 'bg-primary text-white' : 'border border-slate-200 hover:bg-slate-50'}`}>
                {p}
              </button>
            );
          })}
          <button onClick={() => goPage(page + 1)} disabled={page >= data.pages} className="p-2 border border-slate-200 rounded-md hover:bg-slate-50 disabled:opacity-30 transition-colors">
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50" data-testid="voce-modal">
          <div className="bg-white rounded-t-2xl sm:rounded-md w-full sm:max-w-lg max-h-[92vh] sm:max-h-[85vh] overflow-y-auto p-5 sm:p-6 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">{editing ? 'Modifica Voce' : 'Nuova Voce Prezzario'}</h3>
              <button onClick={() => setShowForm(false)}><X size={20} className="text-slate-400" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Codice</label>
                <input data-testid="voce-codice" placeholder="Es: AZ.001" className="w-full px-3 py-2.5 border border-slate-200 rounded-md focus:ring-2 focus:ring-primary/50" value={form.codice} onChange={e => setForm({...form, codice: e.target.value})} />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Descrizione</label>
                <textarea data-testid="voce-descrizione" placeholder="Descrizione della voce" rows={3} className="w-full px-3 py-2.5 border border-slate-200 rounded-md focus:ring-2 focus:ring-primary/50" value={form.descrizione} onChange={e => setForm({...form, descrizione: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Unità di Misura</label>
                  <input data-testid="voce-um" placeholder="Es: mq, ml, cad" className="w-full px-3 py-2.5 border border-slate-200 rounded-md focus:ring-2 focus:ring-primary/50" value={form.unita_misura} onChange={e => setForm({...form, unita_misura: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Prezzo (EUR)</label>
                  <input data-testid="voce-prezzo" type="number" step="0.01" placeholder="0.00" className="w-full px-3 py-2.5 border border-slate-200 rounded-md focus:ring-2 focus:ring-primary/50" value={form.prezzo} onChange={e => setForm({...form, prezzo: e.target.value})} />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Categoria</label>
                <input placeholder="Es: Opere Murarie, Impianti" className="w-full px-3 py-2.5 border border-slate-200 rounded-md focus:ring-2 focus:ring-primary/50" value={form.categoria} onChange={e => setForm({...form, categoria: e.target.value})} list="categorie-list" />
                <datalist id="categorie-list">
                  {categorie.map(c => <option key={c.nome} value={c.nome} />)}
                </datalist>
              </div>
              <button data-testid="save-voce-btn" onClick={save} className="w-full py-2.5 bg-primary text-white font-bold rounded-md hover:bg-primary-hover transition-all active:scale-95">
                {editing ? 'Salva Modifiche' : 'Aggiungi Voce'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
